import { ref, reactive, onActivated, nextTick } from 'vue';
import { ElMessage } from 'element-plus';
import { Edit } from '@element-plus/icons-vue';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useExchangeRecordList() {
  const listRef = ref(null);
  const dialogVisible = ref(false);
  const formRef = ref(null);

  const searchForm = reactive({
    recordId: '',
    userPhone: '',
    itemId: '',
    status: ''
  });

  const {
    listShow,
    handleReset,
    handleSelectionChange,
    handleCurrentRowChange
  } = listCommon(searchForm);

  const FORM_DEFAULT = {
    id: '',
    recordId: '',
    userName: '',
    userPhone: '',
    itemId: '',
    itemName: '',
    itemValue: 0,
    requiredPoints: 0,
    expressSupport: '0',
    receiverName: '',
    receiverPhone: '',
    receiverAddress: '',
    status: '0',
    remark: ''
  };

  const formData = reactive({ ...FORM_DEFAULT });

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        recordId: searchForm.recordId,
        userPhone: searchForm.userPhone,
        itemId: searchForm.itemId,
        status: searchForm.status
      };
      const res = await request.get('/exchange/record/getPage', { params });
      if (listRef.value) {
        if (res.code === 200) {
          listRef.value.updateListData(res.data.records || []);
          listRef.value.updatePagination({
            pageNum: paginationParams.pageNum,
            pageSize: paginationParams.pageSize,
            total: res.data.total || 0
          });
        } else {
          ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
          listRef.value.updateListData([]);
          listRef.value.updatePagination({ ...paginationParams, total: 0 });
        }
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      if (listRef.value) {
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/exchange/record/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.recordId = res.data.recordId || '';
      formData.userName = res.data.userName || '';
      formData.userPhone = res.data.userPhone || '';
      formData.itemId = res.data.itemId || '';
      formData.itemName = res.data.itemName || '';
      formData.itemValue = res.data.itemValue ?? 0;
      formData.requiredPoints = res.data.requiredPoints ?? 0;
      formData.expressSupport = res.data.expressSupport || '0';
      formData.receiverName = res.data.receiverName || '';
      formData.receiverPhone = res.data.receiverPhone || '';
      formData.receiverAddress = res.data.receiverAddress || '';
      formData.status = res.data.status || '0';
      formData.remark = res.data.remark || '';

      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const submitData = {
      id: formData.id,
      receiverName: formData.receiverName,
      receiverPhone: formData.receiverPhone,
      receiverAddress: formData.receiverAddress,
      status: formData.status,
      remark: formData.remark
    };

    listRef.value?.setLoading(true);
    try {
      const res = await request.post('/exchange/record/update', submitData);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success('修改成功');
          dialogVisible.value = false;
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          ElMessage.error('修改失败：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      ElMessage.error('接口异常：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  onActivated(async () => {
    if (listRef.value?.isInited) {
      const pagination = listRef.value.getPagination();
      handleLoadList(pagination);
    }
  });

  return {
    searchForm,
    listRef,
    dialogVisible,
    formRef,
    listShow,
    formData,
    Edit,
    handleSelectionChange,
    handleCurrentRowChange,
    handleEdit,
    handleLoadList,
    handleReset,
    handleSubmit
  };
}
